import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase.js';
import BrandSpinner from '../components/ui/atoms/BrandSpinner/BrandSpinner';
import ProfileTabs from '../components/ProfileTabs';
import { getAvatarURL } from '../utils/avatar';
import { FaGear, FaBell } from "react-icons/fa6";
import { FaQuoteLeft } from 'react-icons/fa6';
import fetchWithAuth from '../utils/fetchWithAuth';
import FollowButton from '../components/ui/atoms/FollowButton';
import { useNotifications } from '../context/NotificationContext';
import { PiCookingPot } from 'react-icons/pi';
import FixedBottomCTA from '../components/ui/atoms/Button/FixedBottomCTA';
import BackButton from '../components/ui/atoms/BackButton';
import ReportModal from '../components/ui/ReportModal';
import { FaFlag } from "react-icons/fa6";
import { useToast } from '../context/ToastContext';

type Followers = {
  username: string;
  name: string;
  id: string;
};

type User = {
  bio: string;
  username: string;
  name: string;
  avatarUrl: string;
};

const Profile: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isOwnProfile = !id;
  const profileId = id || auth.currentUser?.uid!;
  const navigate = useNavigate();

  // menu open state & ref for outside clicks
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const { showToast } = useToast() as any;
  const [justBlockedUserId, setJustBlockedUserId] = useState<string | null>(null);

  // close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // user data
  const [user, setUser] = useState<User | null>(null);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [followersList, setFollowersList] = useState<Followers[]>([]);
  const [followingList, setFollowingList] = useState<Followers[]>([]);
  const { unreadCount } = useNotifications();
  const [isFollowing, setIsFollowing] = useState(false);

  // modals & edit state
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);

  // fetch profile + followers/following
  useEffect(() => {
    if (!profileId) return;
    (async () => {
      // fetch user + isFollowing flag
      const raw = await fetchWithAuth(`/api/users/${profileId}`);
      const { isFollowing: alreadyFollowing, ...u } = raw;
      setUser(u as User);
      setIsFollowing(alreadyFollowing);
      const fol = await fetchWithAuth(`/api/users/${profileId}/followers`);
      setFollowersCount(fol.count);
      setFollowersList(fol.users);
      const fow = await fetchWithAuth(`/api/users/${profileId}/following`);
      setFollowingCount(fow.count);
      setFollowingList(fow.users);
    })();
  }, [profileId]);

  const handleToggleFollow = async () => {
    if (!id) return;
    await fetchWithAuth(
      `/api/users/${id}/${isFollowing ? 'unfollow' : 'follow'}`,
      { method: 'POST' }
    );
    setIsFollowing(!isFollowing);
    // update follower count
    const fol = await fetchWithAuth(`/api/users/${profileId}/followers`);
    setFollowersCount(fol.count);
  };

  if (!user) {
    return (
      <div className="text-center mt-10">
        <BrandSpinner />
      </div>
    );
  }

  return (
    <div className="px-4 py-4 pb-28 max-w-3xl mx-auto">
      {!isOwnProfile && <BackButton />}
      {/* Header */}
      <div className="relative mb-4">
        {/* Top-right icons */}
        {isOwnProfile && (
          <div ref={menuRef} className="absolute top-0 right-0 flex z-10 gap-2">
            <Link
              to="/app/notifications"
              className="relative p-1 rounded-full hover:bg-gray-200"
            >
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-600 rounded-full" />
              )}
              <FaBell className="w-5 h-5 text-gray-800" />
            </Link>
            <button
              onClick={() => navigate('/app/settings')}
              className="p-1 rounded-full hover:bg-gray-200"
            >
              <FaGear className="w-5 h-5 text-gray-800" />
            </button>
          </div>
        )}

        {/* Avatar + Name grouped on left, flag on right */}
        <div className="flex items-center justify-between mb-4">
          {/* left side: avatar + name */}
          <div className="flex items-center">
            <img
              src={getAvatarURL(user)}
              alt={user.name}
              className="w-20 h-20 rounded-full object-cover mr-4"
            />
            <div className="pt-4">
              <h1 className="text-xl font-bold text-gray-800 leading-snug truncate max-w-[200px] sm:max-w-xs">
                {user.name}
              </h1>
              <p className="text-sm text-gray-500">@{user.username}</p>
            </div>
          </div>

          {/* right side: flag icon */}
          {!isOwnProfile && (
            <button
              onClick={() => setShowReportModal(true)}
              className="p-2 text-red-500 hover:bg-gray-100 rounded-full"
              aria-label="Report user"
            >
              <FaFlag className="w-5 h-5 text-red-800" />
            </button>
          )}
        </div>
      </div>

      {/* Bio */}
      {user.bio && (
        <div className="flex items-start gap-2 text-gray-600 mb-4">
          <span className="text-xl pt-1 text-gray-500">
            <FaQuoteLeft className="h-4" />
          </span>
          <p className="text-[15px] leading-snug whitespace-pre-wrap mt-0.5">
            {user.bio}
          </p>
        </div>
      )}

      {/* Follow stats + button */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-6 text-sm">
          <button
            onClick={() => followersCount > 0 && setShowFollowersModal(true)}
            className={`${
              followersCount > 0
                ? 'text-primary hover:underline'
                : 'text-gray-400 cursor-default'
            }`}
          >
            <strong>{followersCount}</strong> Followers
          </button>
          <button
            onClick={() => followingCount > 0 && setShowFollowingModal(true)}
            className={`${
              followingCount > 0
                ? 'text-primary hover:underline'
                : 'text-gray-400 cursor-default'
            }`}
          >
            <strong>{followingCount}</strong> Following
          </button>
        </div>

        {isOwnProfile ? null : (
          <FollowButton
            userId={id}
            mode="text"
            isFollowing={isFollowing}
            onClick={handleToggleFollow}
          />
        )}
      </div>


      {/* Tabs */}
      <ProfileTabs profileId={profileId} />

      {/* Create Recipe CTA */}
      {isOwnProfile && (
        <FixedBottomCTA
          to="/app/create"
          icon={<PiCookingPot className="w-5 h-5" />}
          text="Create Recipe"
        />
      )}

      {/* Followers Modal */}
      {showFollowersModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          onClick={() => setShowFollowersModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg max-w-sm w-full max-h-[80vh] overflow-y-auto p-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold mb-4">Followers</h2>
            {followersList.map((u) => (
              <Link
                key={u.id}
                to={`/app/profile/${u.id}`}
                className="flex items-center mb-3"
                onClick={() => setShowFollowersModal(false)}
              >
                <img
                  src={getAvatarURL(u)}
                  alt={u.name}
                  className="w-8 h-8 rounded-full mr-3"
                />
                <div>
                  <p className="font-medium">{u.name}</p>
                  <p className="text-sm text-gray-500">@{u.username}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Following Modal */}
      {showFollowingModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          onClick={() => setShowFollowingModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg max-w-sm w-full max-h-[80vh] overflow-y-auto p-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold mb-4">Following</h2>
            {followingList.map((u) => (
              <Link
                key={u.id}
                to={`/app/profile/${u.id}`}
                className="flex items-center mb-3"
                onClick={() => setShowFollowingModal(false)}
              >
                <img
                  src={getAvatarURL(u)}
                  alt={u.name}
                  className="w-8 h-8 rounded-full mr-3"
                />
                <div>
                  <p className="font-medium">{u.name}</p>
                  <p className="text-sm text-gray-500">@{u.username}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {showReportModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          onClick={() => setShowReportModal(false)}
        >
          <div
            className="bg-white p-4 rounded-lg shadow-lg w-[90%] max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <ReportModal
              isOpen={showReportModal}
              onClose={() => setShowReportModal(false)}
              type="USER"
              targetId={profileId}
              onSubmit={({ blockUser, targetId }) => {
                if (blockUser) {
                  setJustBlockedUserId(targetId);
                  showToast({ message: "User reported — blocked", type: "success" });
                } else {
                  showToast({ message: "User reported", type: "success" });
                }
                // navigate away: either to your own profile or home
                navigate(`/app/profile`);
              }}
            />
          </div>
        </div>
      )}
      {justBlockedUserId && (
        <button
          onClick={async () => {
            await fetchWithAuth(`/api/blocks/${justBlockedUserId}`, { method: 'DELETE' });
            showToast({ message: "Unblocked user", type: "info" });
            setJustBlockedUserId(null);
          }}
          className="fixed bottom-24 right-4 bg-gray-800 text-white px-3 py-1 rounded"
        >
          Undo block
        </button>
      )}
    </div>
  );
};

export default Profile;
